import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Check, Zap, Crown, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingModal = ({ onClose }) => {
  const [plans, setPlans] = useState({});
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        axios.get(`${API}/subscription/plans`),
        axios.get(`${API}/subscription/my`)
      ]);
      setPlans(plansRes.data.plans);
      setCurrentSubscription(subRes.data);
    } catch (error) {
      console.error('Error fetching pricing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    if (planId === 'free' || planId === currentSubscription?.plan_id) return;

    setProcessingPlan(planId);
    try {
      const response = await axios.post(`${API}/checkout/session`, {
        package_type: 'subscription',
        package_id: planId,
        host_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('Failed to initiate payment. Please try again.');
    } finally {
      setProcessingPlan(null);
    }
  };

  const handleBuyCredits = async (packageId) => {
    setProcessingPlan(packageId);
    try {
      const response = await axios.post(`${API}/checkout/session`, {
        package_type: 'credits',
        package_id: packageId,
        host_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('Failed to initiate payment. Please try again.');
    } finally {
      setProcessingPlan(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'free': return <Zap className="w-6 h-6" />;
      case 'pro': return <Crown className="w-6 h-6" />;
      case 'enterprise': return <Sparkles className="w-6 h-6" />;
      default: return <Zap className="w-6 h-6" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'free': return '#6B7280';
      case 'pro': return '#3B82F6';
      case 'enterprise': return '#8B5CF6';
      default: return '#6B7280';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      style={{ pointerEvents: 'auto' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-2xl p-8 max-w-6xl w-full my-8"
        style={{ pointerEvents: 'auto' }}
      >
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-black mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Choose Your Plan
            </h2>
            <p className="text-sm" style={{ color: '#6B7280' }}>
              Unlock premium features and get more calling minutes
            </p>
          </div>
          <button
            data-testid="close-pricing-button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-black/5"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {currentSubscription && (
          <div className="mb-6 p-4 rounded-xl" style={{ background: '#F9F9FB' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: '#6B7280' }}>Current Plan</p>
                <p className="text-xl font-bold capitalize">{currentSubscription.plan_id}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium" style={{ color: '#6B7280' }}>Minutes Remaining</p>
                <p className="text-2xl font-bold" style={{ color: '#3B82F6' }}>
                  {currentSubscription.call_minutes_remaining || 0}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Plans */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {Object.entries(plans).map(([planId, plan]) => (
            <motion.div
              key={planId}
              whileHover={{ y: -5 }}
              className={`p-6 rounded-2xl border-2 ${
                currentSubscription?.plan_id === planId
                  ? 'border-[#3B82F6] shadow-lg'
                  : 'border-black/5 hover:border-[#3B82F6]/30'
              }`}
              style={{ background: currentSubscription?.plan_id === planId ? '#F0F9FF' : 'white' }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ background: `${getPlanColor(planId)}20`, color: getPlanColor(planId) }}
                >
                  {getPlanIcon(planId)}
                </div>
                <div>
                  <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {plan.name}
                  </h3>
                  <p className="text-2xl font-black" style={{ color: '#111111' }}>
                    ${plan.price}
                    <span className="text-sm font-normal" style={{ color: '#6B7280' }}>/month</span>
                  </p>
                </div>
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: getPlanColor(planId) }} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                data-testid={`upgrade-${planId}-button`}
                onClick={() => handleUpgrade(planId)}
                disabled={processingPlan === planId || currentSubscription?.plan_id === planId || planId === 'free'}
                className={`w-full py-3 rounded-full font-semibold transition-all ${
                  currentSubscription?.plan_id === planId
                    ? 'bg-[#3B82F6] text-white cursor-default'
                    : planId === 'free'
                    ? 'bg-black/5 text-black/40 cursor-not-allowed'
                    : 'bg-[#111111] text-white hover:scale-105 active:scale-95'
                }`}
              >
                {processingPlan === planId
                  ? 'Processing...'
                  : currentSubscription?.plan_id === planId
                  ? 'Current Plan'
                  : planId === 'free'
                  ? 'Default Plan'
                  : 'Upgrade'}
              </button>
            </motion.div>
          ))}
        </div>

        {/* Call Credits */}
        <div className="border-t pt-6" style={{ borderColor: 'rgba(0,0,0,0.05)' }}>
          <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Or Buy Call Credits
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { id: 'small', minutes: 30, price: 5.00, label: 'Starter' },
              { id: 'medium', minutes: 100, price: 15.00, label: 'Popular', highlight: true },
              { id: 'large', minutes: 300, price: 40.00, label: 'Best Value' }
            ].map((pkg) => (
              <motion.div
                key={pkg.id}
                whileHover={{ scale: 1.02 }}
                className={`p-4 rounded-xl border-2 ${
                  pkg.highlight ? 'border-[#3B82F6] shadow-md' : 'border-black/5'
                }`}
                style={{ background: pkg.highlight ? '#F0F9FF' : '#F9F9FB' }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold" style={{ color: '#6B7280' }}>{pkg.label}</span>
                  <span className="text-2xl font-bold">${pkg.price}</span>
                </div>
                <p className="text-3xl font-black mb-3" style={{ color: '#3B82F6' }}>
                  {pkg.minutes} min
                </p>
                <button
                  data-testid={`buy-credits-${pkg.id}-button`}
                  onClick={() => handleBuyCredits(pkg.id)}
                  disabled={processingPlan === pkg.id}
                  className="w-full py-2 rounded-full font-semibold bg-[#111111] text-white hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
                >
                  {processingPlan === pkg.id ? 'Processing...' : 'Buy Now'}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PricingModal;
